/**
 * Campaign Context
 * Manages current campaign state for multi-campaign support.
 * Campaigns belong to organizations.
 */

import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from './supabase';
import { useOrganization } from './organization-context';

export interface Campaign {
  id: string;
  organization_id: string;
  name: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface CampaignContextType {
  /** Currently selected campaign, or null for "All Campaigns" */
  currentCampaign: Campaign | null;
  /** All campaigns for the current organization */
  campaigns: Campaign[];
  /** Whether campaigns are being loaded */
  loading: boolean;
  /** Switch to a specific campaign, or null for "All Campaigns" */
  switchCampaign: (campaignId: string | null) => void;
  /** Refresh the campaign list from the server */
  refreshCampaigns: () => Promise<void>;
  /** Get the current campaign ID for filtering queries */
  currentCampaignId: string | null;
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

const STORAGE_KEY = 'prozpector_current_campaign_id';

export function CampaignProvider({ children }: { children: ReactNode }) {
  const { currentOrganization, loading: orgLoading } = useOrganization();
  const [currentCampaign, setCurrentCampaign] = useState<Campaign | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCampaigns = useCallback(async () => {
    if (!currentOrganization?.id) {
      setCampaigns([]);
      setCurrentCampaign(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .neq('status', 'archived')
        .order('name', { ascending: true });

      if (error) throw error;

      setCampaigns(data || []);

      // Restore selected campaign from localStorage
      const savedCampaignId = localStorage.getItem(STORAGE_KEY);

      if (savedCampaignId && savedCampaignId !== 'all') {
        const savedCampaign = (data || []).find(c => c.id === savedCampaignId);
        if (savedCampaign) {
          setCurrentCampaign(savedCampaign);
        } else {
          // Saved campaign not found, default to "All Campaigns"
          setCurrentCampaign(null);
          localStorage.setItem(STORAGE_KEY, 'all');
        }
      } else {
        // Default to "All Campaigns"
        setCurrentCampaign(null);
        localStorage.setItem(STORAGE_KEY, 'all');
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id]);

  const switchCampaign = useCallback((campaignId: string | null) => {
    if (campaignId === null) {
      // Switch to "All Campaigns"
      setCurrentCampaign(null);
      localStorage.setItem(STORAGE_KEY, 'all');
    } else {
      const campaign = campaigns.find(c => c.id === campaignId);
      if (campaign) {
        setCurrentCampaign(campaign);
        localStorage.setItem(STORAGE_KEY, campaignId);
      }
    }
  }, [campaigns]);

  const refreshCampaigns = useCallback(async () => {
    setLoading(true);
    await fetchCampaigns();
  }, [fetchCampaigns]);

  // Fetch campaigns when organization changes
  useEffect(() => {
    if (!orgLoading) {
      fetchCampaigns();
    }
  }, [fetchCampaigns, orgLoading, currentOrganization?.id]);

  // Update currentCampaign when campaigns list changes
  useEffect(() => {
    if (currentCampaign) {
      const updated = campaigns.find(c => c.id === currentCampaign.id);
      if (updated) {
        setCurrentCampaign(updated);
      } else if (campaigns.length > 0) {
        // Current campaign was deleted, switch to "All Campaigns"
        setCurrentCampaign(null);
        localStorage.setItem(STORAGE_KEY, 'all');
      }
    }
  }, [campaigns, currentCampaign?.id]);

  return (
    <CampaignContext.Provider
      value={{
        currentCampaign,
        campaigns,
        loading,
        switchCampaign,
        refreshCampaigns,
        currentCampaignId: currentCampaign?.id || null,
      }}
    >
      {children}
    </CampaignContext.Provider>
  );
}

export function useCampaign() {
  const context = useContext(CampaignContext);
  if (context === undefined) {
    throw new Error('useCampaign must be used within a CampaignProvider');
  }
  return context;
}

/**
 * Hook to get the current campaign ID for use in queries.
 * Returns null when "All Campaigns" is selected.
 */
export function useCampaignId(): string | null {
  const { currentCampaignId } = useCampaign();
  return currentCampaignId;
}

/**
 * Hook to add campaign_id filter to Supabase queries.
 * Returns a function that adds the filter if a campaign is selected.
 */
export function useCampaignFilter() {
  const { currentCampaignId } = useCampaign();

  return useCallback(<T extends { eq: (column: string, value: string) => T }>(
    query: T
  ): T => {
    if (currentCampaignId) {
      return query.eq('campaign_id', currentCampaignId);
    }
    return query;
  }, [currentCampaignId]);
}
